import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  Book,
  Plus,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  Trash2,
  Settings,
  Loader2,
  X,
  Download,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface KnowledgeSource {
  id: string;
  projectId: string;
  type: 'CONFLUENCE' | 'NOTION' | 'SHAREPOINT';
  name: string;
  spaceUrl?: string;
  spaceKey?: string;
  baseUrl?: string;
  scope: { mode: string; rootPageId?: string };
  chunking: { mode: string; maxChars: number };
  enableTaxonomy: boolean;
  enableEmbeddings: boolean;
  syncSchedule?: string;
  lastSyncAt?: string;
  lastSyncStatus?: 'SUCCESS' | 'PARTIAL' | 'FAILED';
  status: 'ACTIVE' | 'PAUSED' | 'ERROR';
  errorMessage?: string;
  stats: {
    pagesCount: number;
    chunksCount: number;
    entitiesCount: number;
  };
  createdAt: string;
}

interface SyncRun {
  runId: string;
  sourceId: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'PARTIAL' | 'FAILED';
  mode: string;
  progress: number;
  startedAt?: string;
  finishedAt?: string;
  stats: {
    pagesFetched: number;
    pagesChanged: number;
    chunksUpserted: number;
  };
}

interface Project {
  id: string;
  orgId: string;
  orgName: string;
  name: string;
}

// ============================================
// Add Confluence Modal
// ============================================

function AddConfluenceModal({
  projectId,
  onClose,
  onSuccess,
}: {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [spaceUrl, setSpaceUrl] = useState('');
  const [spaceKey, setSpaceKey] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; spaceName?: string; error?: string } | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post<KnowledgeSource>('/knowledge-sources/confluence', {
        projectId,
        name,
        spaceUrl,
        auth: { email, token },
      });
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to create source');
    },
  });

  const handleTest = async () => {
    if (!spaceUrl || !email || !token) {
      setError('Please fill all fields first');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    setError('');
    
    try {
      // First create, then test
      const source = await api.post<KnowledgeSource>('/knowledge-sources/confluence', {
        projectId,
        name: name || 'Test Connection',
        spaceUrl,
        spaceKey: spaceKey || undefined,
        auth: { email, token },
      });
      
      const result = await api.post<{ success: boolean; spaceName?: string; error?: string }>(
        `/knowledge-sources/${source.id}/test`
      );
      
      setTestResult(result);
      
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        // Delete the source if test failed
        await api.delete(`/knowledge-sources/${source.id}`);
        setError(result.error || 'Connection test failed');
      }
    } catch (err: any) {
      setError(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg animate-fade-in">
        <div className="card-header justify-between">
          <h3 className="card-title flex items-center gap-2">
            <Book className="w-5 h-5" style={{ color: 'var(--primary)' }} />
            Connect Confluence Space
          </h3>
          <button onClick={onClose} className="btn btn-ghost p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Display Name
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g., Product Requirements"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Confluence Space URL
            </label>
            <input
              type="url"
              className="input w-full"
              placeholder="https://company.atlassian.net/wiki/spaces/PROD"
              value={spaceUrl}
              onChange={(e) => setSpaceUrl(e.target.value)}
            />
            <p className="text-xs text-muted mt-1">
              Paste the URL of any page in your Confluence space
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-default mb-1">
              Space Key <span className="text-muted font-normal">(optional)</span>
            </label>
            <input
              type="text"
              className="input w-full"
              placeholder="e.g., PROD or ~712020c87d..."
              value={spaceKey}
              onChange={(e) => setSpaceKey(e.target.value)}
            />
            <p className="text-xs text-muted mt-1">
              Leave empty to auto-detect from URL. For personal spaces, use the full key starting with ~
            </p>
          </div>
          
          <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
            <p className="text-sm font-medium text-default mb-3">
              Authentication (Personal Access Token)
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-muted mb-1">
                  Atlassian Email
                </label>
                <input
                  type="email"
                  className="input w-full"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm text-muted mb-1">
                  API Token
                </label>
                <input
                  type="password"
                  className="input w-full"
                  placeholder="ATATT3xFfGF0..."
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                >
                  Create API Token
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-danger/10 text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          {testResult?.success && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-success/10 text-success">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">Connected to: {testResult.spaceName}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleTest}
            disabled={testing || createMutation.isPending || !spaceUrl || !email || !token}
            className="btn btn-primary"
          >
            {testing || createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Connect & Sync
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Source Card Component (Enhanced Design)
// ============================================

function SourceCard({
  source,
  onSync,
  onDelete,
  onExport,
  isWide = false,
}: {
  source: KnowledgeSource;
  onSync: () => void;
  onDelete: () => void;
  onExport: () => void;
  isWide?: boolean;
}) {
  const getStatusBadge = () => {
    if (source.status === 'ERROR') {
      return <span className="badge badge-danger">Error</span>;
    }
    if (source.status === 'PAUSED') {
      return <span className="badge badge-neutral">Paused</span>;
    }
    if (source.lastSyncStatus === 'SUCCESS') {
      return <span className="badge badge-success">Synced</span>;
    }
    if (source.lastSyncStatus === 'PARTIAL') {
      return <span className="badge badge-warning">Partial</span>;
    }
    if (source.lastSyncStatus === 'FAILED') {
      return <span className="badge badge-danger">Failed</span>;
    }
    return <span className="badge badge-neutral">Pending</span>;
  };

  const getTypeIcon = () => {
    switch (source.type) {
      case 'CONFLUENCE':
        return (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" 
               style={{ background: 'linear-gradient(135deg, #0052CC 0%, #0065FF 100%)' }}>
            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.4 17.4c-.2.3-.1.7.2.9l4.5 2.9c.3.2.7.1.9-.2 1.4-2.5 3.7-2.7 7.2-1.1.3.1.6 0 .8-.2l4.4-3.1c.3-.2.3-.6.2-.9-2.8-4.4-8.3-4.2-13.7-.3-2.3 1.7-3.7 1.8-4.5 0zm19.2-10.8c.2-.3.1-.7-.2-.9l-4.5-2.9c-.3-.2-.7-.1-.9.2-1.4 2.5-3.7 2.7-7.2 1.1-.3-.1-.6 0-.8.2L3.6 7.4c-.3.2-.3.6-.2.9 2.8 4.4 8.3 4.2 13.7.3 2.3-1.7 3.7-1.8 4.5 0z"/>
            </svg>
          </div>
        );
      case 'NOTION':
        return (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-neutral-900 dark:bg-white">
            <svg className="w-7 h-7 text-white dark:text-neutral-900" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.84-.046.933-.56.933-1.167V6.354c0-.606-.233-.933-.746-.886l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.747 0-.933-.234-1.494-.933l-4.577-7.186v6.952l1.447.327s0 .84-1.168.84l-3.22.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.454-.234 4.763 7.279v-6.44l-1.215-.14c-.093-.514.28-.886.747-.933zM2.718 1.321l13.542-.933c1.634-.14 2.055-.047 3.08.7l4.25 2.986c.7.513.933.653.933 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.933c-.98.047-1.448-.093-1.962-.747l-3.127-4.06c-.56-.747-.793-1.306-.793-1.96V2.948c0-.84.373-1.54 1.215-1.626z"/>
            </svg>
          </div>
        );
      case 'SHAREPOINT':
        return (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
               style={{ background: 'linear-gradient(135deg, #038387 0%, #00B294 100%)' }}>
            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.5 2a8.5 8.5 0 0 1 8.38 9.8 6.5 6.5 0 0 1-1.38 12.2H6.5A6.5 6.5 0 0 1 5.12 11.8 8.5 8.5 0 0 1 11.5 2z"/>
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-neutral-200 dark:bg-neutral-700">
            <Book className="w-7 h-7 text-neutral-600 dark:text-neutral-300" />
          </div>
        );
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  // Wide layout for single card
  if (isWide) {
    return (
      <div className="card group hover:border-primary transition-all">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          {/* Left: Source Info */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {getTypeIcon()}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h4 className="font-semibold text-lg text-default truncate">{source.name}</h4>
                {getStatusBadge()}
              </div>
              <p className="text-sm text-muted truncate">{source.spaceKey || source.type}</p>
            </div>
          </div>

          {/* Center: Stats */}
          <div className="flex items-center gap-8 lg:gap-12">
            <div className="text-center">
              <p className="text-3xl font-bold text-default">{source.stats.pagesCount}</p>
              <p className="text-xs text-muted uppercase tracking-wider">Pages</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-default">{source.stats.chunksCount}</p>
              <p className="text-xs text-muted uppercase tracking-wider">Chunks</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-default">{source.stats.entitiesCount}</p>
              <p className="text-xs text-muted uppercase tracking-wider">Entities</p>
            </div>
          </div>

          {/* Right: Meta & Actions */}
          <div className="flex flex-col lg:items-end gap-3">
            <div className="flex items-center gap-2 text-sm text-muted">
              <Clock className="w-4 h-4" />
              Last sync: {formatDate(source.lastSyncAt)}
            </div>
            
            {source.spaceUrl && (
              <a
                href={source.spaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Open in Confluence
                <ExternalLink className="w-3 h-3" />
              </a>
            )}

            <div className="flex items-center gap-2">
              <button onClick={onSync} className="btn btn-secondary">
                <RefreshCw className="w-4 h-4" />
                Sync Now
              </button>
              <button onClick={onExport} className="btn btn-ghost p-2" title="Export chunks">
                <Download className="w-4 h-4" />
              </button>
              <button className="btn btn-ghost p-2" title="Settings">
                <Settings className="w-4 h-4" />
              </button>
              <button onClick={onDelete} className="btn btn-ghost p-2 text-danger hover:bg-danger/10" title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {source.errorMessage && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 text-danger text-sm mt-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p>{source.errorMessage}</p>
          </div>
        )}
      </div>
    );
  }

  // Compact card layout (for multiple cards)
  return (
    <div className="card group hover:border-primary transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {getTypeIcon()}
          <div>
            <h4 className="font-medium text-default">{source.name}</h4>
            <p className="text-sm text-muted">{source.spaceKey || source.type}</p>
          </div>
        </div>
        {getStatusBadge()}
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-3 rounded-lg" style={{ background: 'var(--surface-secondary)' }}>
          <p className="text-2xl font-bold text-default">{source.stats.pagesCount}</p>
          <p className="text-xs text-muted">Pages</p>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ background: 'var(--surface-secondary)' }}>
          <p className="text-2xl font-bold text-default">{source.stats.chunksCount}</p>
          <p className="text-xs text-muted">Chunks</p>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ background: 'var(--surface-secondary)' }}>
          <p className="text-2xl font-bold text-default">{source.stats.entitiesCount}</p>
          <p className="text-xs text-muted">Entities</p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-muted mb-4">
        <Clock className="w-4 h-4" />
        Last sync: {formatDate(source.lastSyncAt)}
      </div>
      
      {source.errorMessage && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-danger/10 text-danger text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p className="truncate">{source.errorMessage}</p>
        </div>
      )}
      
      {source.spaceUrl && (
        <a
          href={source.spaceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-primary hover:underline mb-4"
        >
          Open in Confluence
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
      
      <div className="flex items-center gap-2 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <button onClick={onSync} className="btn btn-secondary flex-1">
          <RefreshCw className="w-4 h-4" />
          Sync Now
        </button>
        <button onClick={onExport} className="btn btn-ghost p-2" title="Export chunks for debugging">
          <Download className="w-4 h-4" />
        </button>
        <button className="btn btn-ghost p-2">
          <Settings className="w-4 h-4" />
        </button>
        <button onClick={onDelete} className="btn btn-ghost p-2 text-danger hover:bg-danger/10">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================
// Main Page
// ============================================

export function KnowledgeSourcesPage() {
  const { projectId } = useParams<{ orgId: string; projectId: string }>();
  const [showAddModal, setShowAddModal] = useState(false);
  const queryClient = useQueryClient();
  const { setHeader, clearHeader } = usePageHeaderStore();
  
  const { data: _project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  const { data: sources, isLoading } = useQuery({
    queryKey: ['knowledge-sources', projectId],
    queryFn: () => api.get<{ data: KnowledgeSource[] }>(`/knowledge-sources?projectId=${projectId}`),
    enabled: !!projectId,
  });
  
  useEffect(() => {
    setHeader(
      'Knowledge Base',
      'Connect external documentation to improve AI-generated requirements',
      <Book className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
      undefined,
      {
        actions: [
          ...(sources?.data && sources.data.length > 0 ? [{
            id: 'export-chunks',
            label: 'Export Chunks',
            icon: <Download className="w-4 h-4" />,
            variant: 'secondary' as const,
            onClick: () => handleExport(),
          }] : []),
          {
            id: 'connect-source',
            label: 'Connect Source',
            icon: <Plus className="w-4 h-4" />,
            variant: 'primary' as const,
            onClick: () => setShowAddModal(true),
          },
        ],
      }
    );
    return () => clearHeader();
  }, [sources?.data, setHeader, clearHeader]);
  
  const syncMutation = useMutation({
    mutationFn: (sourceId: string) => api.post<SyncRun>(`/knowledge-sources/${sourceId}/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources', projectId] });
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: (sourceId: string) => api.delete(`/knowledge-sources/${sourceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources', projectId] });
    },
  });
  
  const handleSync = (sourceId: string) => {
    syncMutation.mutate(sourceId);
  };
  
  const handleDelete = (sourceId: string) => {
    if (confirm('Are you sure you want to delete this knowledge source? All synced data will be removed.')) {
      deleteMutation.mutate(sourceId);
    }
  };
  
  const handleExport = async (sourceId?: string) => {
    try {
      const url = sourceId 
        ? `/knowledge-sources/export/chunks?projectId=${projectId}&sourceId=${sourceId}`
        : `/knowledge-sources/export/chunks?projectId=${projectId}`;
      
      const data = await api.get<any>(url);
      
      // Format for readability
      const formatted = JSON.stringify(data, null, 2);
      
      // Create download
      const blob = new Blob([formatted], { type: 'application/json' });
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `knowledge-chunks-${sourceId || 'all'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Check console for details.');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-1/4" />
        <div className="skeleton h-48 rounded-card" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Sources Grid */}
      {sources?.data && sources.data.length > 0 ? (
        <div className={
          sources.data.length === 1 
            ? "grid grid-cols-1 gap-6" 
            : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        }>
          {sources.data.map((source) => (
            <SourceCard
              key={source.id}
              source={source}
              onSync={() => handleSync(source.id)}
              onDelete={() => handleDelete(source.id)}
              onExport={() => handleExport(source.id)}
              isWide={sources.data.length === 1}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-16">
          <Book className="w-16 h-16 mx-auto mb-4 text-subtle" />
          <h3 className="text-lg font-medium text-default mb-2">No knowledge sources connected</h3>
          <p className="text-muted mb-6 max-w-md mx-auto">
            Connect your Confluence, Notion, or other documentation to help the AI generate 
            requirements that align with your existing specifications.
          </p>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            Connect Confluence
          </button>
        </div>
      )}
      
      {/* Sync Status Toast */}
      {syncMutation.isPending && (
        <div className="fixed bottom-6 right-6 card flex items-center gap-3 animate-fade-in">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--primary)' }} />
          <span className="text-default">Syncing...</span>
        </div>
      )}
      
      {/* Add Modal */}
      {showAddModal && (
        <AddConfluenceModal
          projectId={projectId!}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['knowledge-sources', projectId] });
          }}
        />
      )}
    </div>
  );
}

